'use client';
import { useEffect, useState } from 'react';
import { CuralateRegionalConfig } from '@/components/ui/SocialFeed/SocialFeed';

declare global {
  interface Window {
    crl8: any;
  }
}
async function loadCuralateScript(regionConfig: CuralateRegionalConfig) {
  if (typeof window === 'undefined') return;

  if (window.document.querySelector('#curalate-script')) {
    try {
      await new Promise((resolve) => {
        window.crl8.ready(() => resolve(true));
      });

      await window.crl8.getExperience(regionConfig.id);

      /*
        The following element is a content container element generated
        by Curalate. It will be null if the user navigated away from the
        page and then returned. In this case, the Curalate module had
        initialized from the first view but the React rerender replaced
        the original HTML, effectively setting the component in a non-display
        state. In this case, we need to destroy the Curalate reference and
        re-initialize the module so it will display properly.
      */
      const socialFeedHasContent = document.querySelector(
        '[data-crl8-container-id] *',
      );
      if (socialFeedHasContent) return;

      await window.crl8.destroyExperience(regionConfig.id);
      await window.crl8.createExperience(regionConfig.id);
    } catch (error) {
      console.warn('[Curalate]', error);
    }

    return;
  }

  const script = document.createElement('script');
  script.innerText = regionConfig.script;
  script.async = true;
  script.id = 'curalate-script';
  document.body.appendChild(script);
}

export const useCuralateScript = (regionConfig: CuralateRegionalConfig) => {
  const [scriptFetched, setScriptFetched] = useState(false);

  useEffect(() => {
    // Prevent duplicate script loading in development
    if (process.env.NODE_ENV === 'development') {
      if (!scriptFetched) {
        const rerenderWaitTime = 1000;
        setTimeout(loadCuralateScript, rerenderWaitTime, regionConfig);

        setScriptFetched(true);
      }
    } else {
      loadCuralateScript(regionConfig);
    }
  }, [scriptFetched, regionConfig]);
};
